/*
These are the actions for the page.
Think of these as "event emitters" used by the components on the page.
*/
import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { Action, Dispatch } from 'redux';
import { utilityService } from '@services/UtilityService';
import { AppState } from '../..';
import { ModalProps } from '@common/components/modal/_components/modal.component';
import { AddressViewModel, StatesViewModel, AddressValidationModel, AppDispatcher } from '@models/common.models';
import { contractService } from '@services/ContractService';
import { FileViewModel } from '@models/Document';
import { actions } from 'react-redux-form';
import { config } from '@common/config';
import FormNames from '@constants/FormNames.enum';
import * as Redux from 'redux';
import { DialogProperties } from '@common/components';
import { store } from '../../index';
import { AddressReviewComponent } from '@common/components/address-review/address-review.component';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CommonActionType {
  LoadStatesStart = 'LoadStatesStart',
  LoadStatesFailure = 'LoadStatesFailure',
  LoadStatesSuccess = 'LoadStatesSuccess',

  ToggleModalSync = 'ToggleModalSync',
  ToggleModal = 'ToggleModal',
  ToggleDialog = 'ToggleDialog',
  CommonVerifyAddressStart = 'CommonVerifyAddressStart',
  CommonVerifyAddressSuccess = 'CommonVerifyAddressSuccess',
  CommonVerifyAddressFail = 'CommonVerifyAddressFail',

  /********** AttachFile **********/
  AttachFileStart = 'AttachFileStart',
  AttachFileSuccess = 'AttachFileSuccess',
  AttachFileFailure = 'AttachFileFailure',

  AbstractGetInit = 'AbstractGetInit',
  AbstractGetSuccess = 'AbstractGetSuccess',
  AbstractGetFailure = 'AbstractGetFailure',

  LoadFromAbstractGet = 'LoadFromAbstractGet',

  ResetFileAttachForm = 'ResetFileAttachForm',
  NetworkStart = 'NetworkStart',
  NetworkSuccess = 'NetworkStart',
  NetworkFailure = 'NetworkStart',

  SetFileListLoading = 'SetFileListLoading'
}

export interface StatesAction extends Action {
  readonly type: CommonActionType;
  readonly errorMessage?: string;
  readonly states?: StatesViewModel[];
}

export interface ToggleModalAction extends Action {
  readonly type: CommonActionType.ToggleModal;
  readonly modalProps?: ModalProps;
}

export interface ToggleDialogAction extends Action {
  readonly type: CommonActionType.ToggleDialog;
  readonly dialogProps?: DialogProperties;
}

export interface AddressVerificationAction extends Action {
  readonly type:
    | CommonActionType.CommonVerifyAddressStart
    | CommonActionType.CommonVerifyAddressSuccess
    | CommonActionType.CommonVerifyAddressFail;
  readonly addressToVerify?: AddressViewModel;
  readonly verifiedAddress?: AddressValidationModel;
}

/********** AttachFile **********/
export interface AttachFileStartAction extends Action {
  readonly type: CommonActionType.AttachFileStart;
}
export interface AttachFileSuccessAction extends Action {
  readonly type: CommonActionType.AttachFileSuccess;
  readonly fileVMs: FileViewModel[];
}
export interface AttachFileFailureAction extends Action {
  readonly type: CommonActionType.AttachFileFailure;
  readonly errorMessage: string;
}

export interface SuccessGetResultAction<T> extends Action {
  readonly type: CommonActionType.AbstractGetSuccess;
  readonly id: number;
  readonly resultToStore: T;
}

export interface InitGetResultAction extends Action {
  readonly type: CommonActionType.AbstractGetInit;
  readonly idToGet: number;
}

export interface GetResultFailureAction extends Action {
  readonly type: CommonActionType.AbstractGetFailure;
  readonly idWithError: number;

  readonly message: string;
}

export interface LoadGetResultAction extends Action {
  readonly type: CommonActionType.LoadFromAbstractGet;
  readonly id: number;
}

export interface ResetFileAttachFormAction extends Action {
  readonly type: CommonActionType.ResetFileAttachForm;
}

export interface SetFileListLoadingAction extends Action {
  readonly type: CommonActionType.SetFileListLoading;
  readonly isFileListLoading: boolean;
}

export function doAsync<TAction extends Action = Action>(action: TAction) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(action);
  };
}
// Actions for each action type declared here
export function loadStatesStart() {
  return {
    type: CommonActionType.LoadStatesStart
  };
}

export function loadStatesSuccess(states: StatesViewModel[]): StatesAction {
  return {
    type: CommonActionType.LoadStatesSuccess,
    states: states
  };
}

export function loadStatesFailure(err?: string): StatesAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to load states. Please try again later.'} />,
    ToastOptions.Error
  );
  return {
    type: CommonActionType.LoadStatesFailure,
    errorMessage: err
  };
}

export const loadStates = () => {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState();
    if (state.common && state.common.states) {
      return;
    } // don't load states again

    dispatch(loadStatesStart());
    try {
      const states = await utilityService.getStates();
      dispatch(loadStatesSuccess(states));
    } catch (e) {
      dispatch(loadStatesFailure(e));
    }
  };
};

export function toggleModalSync(modalProps?: ModalProps) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(toggleModal(modalProps));
  };
}

export function toggleModal<ChildProps>(modalProps?: ModalProps<ChildProps>): ToggleModalAction {
  return {
    type: CommonActionType.ToggleModal,
    modalProps: modalProps
  };
}

export function toggleDialog(dialogProps?: DialogProperties): ToggleDialogAction {
  return {
    type: CommonActionType.ToggleDialog,
    dialogProps: dialogProps
  };
}

export function verifyAddressStart(address: AddressViewModel): AddressVerificationAction {
  return {
    type: CommonActionType.CommonVerifyAddressStart,
    addressToVerify: address
  };
}

export function verifyAddressSuccess(verifiedAddress: AddressValidationModel): AddressVerificationAction {
  return {
    type: CommonActionType.CommonVerifyAddressSuccess,
    verifiedAddress: verifiedAddress
  };
}
export function verifyAddressFail(): AddressVerificationAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to verify address. Please try again later.'}
    />,
    ToastOptions.Error
  );

  return {
    type: CommonActionType.CommonVerifyAddressFail
  };
}

export function checkForDuplicateAddress(address: AddressViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    return contractService.checkDuplicateAddresses(address);
  };
}

type ActionDispatch<ReturnT> = (dispatch: AppDispatcher, getState: () => AppState) => Promise<ReturnT | undefined>;

export function registerAddressVerifier(
  formName: FormNames,
  getter: string,
  setter: (baseFormState: any, address: AddressViewModel) => void,
  shouldCheckDuplicates: boolean,
  callback?: Function
): ActionDispatch<Redux.Unsubscribe> {
  return async (dispatch: AppDispatcher, getState: () => AppState): Promise<undefined | Redux.Unsubscribe> => {
    if (!shouldCheckDuplicates) {
      addressVerifier(formName, getter, setter, shouldCheckDuplicates, dispatch, getState, callback);
      return;
    }
    return store.subscribe(() =>
      addressVerifier(formName, getter, setter, shouldCheckDuplicates, dispatch, getState, callback)
    );
  };
}

export function addressVerifier(
  formName: FormNames,
  getter: string,
  setter: (baseFormState: any, address: AddressViewModel) => void,
  shouldCheckDuplicates: boolean,
  dispatch: AppDispatcher,
  getState: () => AppState,
  callback?: Function
) {
  {
    const appState: any = getState();
    const baseFormState = appState.forms[formName];
    const toCheck = baseFormState ? baseFormState[getter] : undefined;
    // now we have the address to verify, do the thing
    if (toCheck === undefined) {
      if (callback) {
        callback();
      }
      return;
    }
    const propsToCheck = [toCheck.line1, toCheck.city, toCheck.state.abbreviation, toCheck.zip];
    const list = propsToCheck.filter((x: any) => {
      return !x.touched || x.focus || !x.value;
    });
    if (list.length > 0 && shouldCheckDuplicates) {
      if (callback) {
        callback();
      }
      return; // do whatever you were gonna do before
    }
    /* if (toCheck.isValidated && toCheck.isValidated.value) {
      return;
    } */
    // check for address validity
    const temp: AddressViewModel = {
      id: 0,
      line1: toCheck.line1.value,
      line2: toCheck.line2 ? toCheck.line2.value : '',
      city: toCheck.city.value,
      state: {
        id: 0,
        abbreviation: toCheck.state.abbreviation.value,
        name: toCheck.state.abbreviation.value
      },
      zip: toCheck.zip.value
    };
    dispatch(verifyAddress(temp, formName, getter, setter, shouldCheckDuplicates, callback));
  }
}

function shouldRevalidateAddress(a1: AddressViewModel | null, a2: AddressViewModel): boolean {
  return (
    !a1 ||
    !(
      a1.line1.toLowerCase() === a2.line1.toLowerCase() &&
      a1.city.toLowerCase() === a2.city.toLowerCase() &&
      a1.zip.toLowerCase() === a2.zip.toLowerCase() &&
      (a1.state || {}).abbreviation === (a2.state || {}).abbreviation
    )
  );
}
let lastAddress: AddressViewModel | null = null;
export function verifyAddress(
  address: AddressViewModel,
  formName: FormNames,
  getter: string,
  setter: (s: any, a: AddressViewModel) => void,
  shouldCheckDuplicates: boolean,
  callback?: Function
) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (!shouldRevalidateAddress(lastAddress, address)) {
      if (callback) {
        callback();
      }
      return;
    }
    lastAddress = address;
    dispatch(verifyAddressStart(address));
    utilityService.validateAddress(address).then((response: AddressValidationModel) => {
      const verifiedAddress: AddressViewModel = {
        id: 0,
        line1: response.addressLine1Ds,
        line2: response.addressLine2Ds,
        // zip: response.zipAn + (response.zip4An ? '-' + response.zip4An : ''),
        zip: response.zipAn,
        city: response.cityNm,
        state: {
          abbreviation: response.stateAbbrNm,
          name: '',
          id: 0
        }
      };
      dispatch(
        toggleModal({
          title: 'Address Review',
          component: AddressReviewComponent,
          hideSave: true,
          saveCallback: () => {
            if (callback) {
              callback();
            }
            return;
          },
          closeCallback: () => {
            if (callback) {
              callback();
            }
          },
          childProps: {
            addressToVerify: address,
            verifiedAddress: verifiedAddress,
            checkForDuplicateAddresses: (anAddress: AddressViewModel) => {
              return dispatch(checkForDuplicateAddress(anAddress));
            },
            returnWithAddress: (returnedAddress: AddressViewModel) => {
              lastAddress = returnedAddress;
              dispatch(actions.change(formName + '.' + getter, returnedAddress));
              dispatch(toggleModal());
            },
            shouldCheckDuplicates: shouldCheckDuplicates
          }
        })
      );
      // for now, assume what you get back is always a valid address, and move on to the reducer.
      dispatch(verifyAddressSuccess(response));
      return verifiedAddress;
    });
  };
}
/*
export function verifyAddress(
  address: AddressViewModel,
  callback: (addressToVerify: AddressViewModel, verifiedAddress?: AddressViewModel) => void
) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(verifyAddressStart(address));

    // some asynchronous validation function that returns a promise
    utilityService
      .validateAddress(address)
      .then((response: AddressValidationModel) => {
        const verifiedAddress: AddressViewModel = {
          id: 0,
          line1: response.addressLine1Ds,
          line2: response.addressLine2Ds,
          // zip: response.zipAn + (response.zip4An ? '-' + response.zip4An : ''),
          zip: response.zipAn,
          city: response.cityNm,
          state: {
            abbreviation: response.stateAbbrNm,
            name: '',
            id: 0
          }
        };
        // for now, assume what you get back is always a valid address, and move on to the reducer.
        dispatch(verifyAddressSuccess(response));
        callback(address, verifiedAddress);
        /*

                const validity = compareAddressWithValid(address, response);
                if (!validity) {
                    dispatch(successfulValidation(''));
                    return;
                }
                dispatch(failedValidation(validity)); *
      })
      .catch(err => {
        dispatch(verifyAddressFail()); // implies that the address could not be verified, so it is not valid
      });
  };
}
*/

/********** attachFile **********/
export function attachFile(files: File[]) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(attachFileStart());
    try {
      var fileVMs: FileViewModel[] = [];
      files.forEach(element => {
        if (element.size === 0) {
          toast.error(
            <ToastComponent
              type={ToastType.Error}
              message={`${element.name} contains 0 bytes and cannot be uploaded.`}
            />,
            ToastOptions.Error
          );
          return;
        }
        var fileVM = { file: element } as FileViewModel;
        fileVMs.push(fileVM);
        dispatch(actions.push('fileUploadForm.fileVMs', fileVM));
      });
      dispatch(attachFileSuccess(fileVMs));
    } catch (e) {
      dispatch(attachFileFailure(e.message));
    }
  };
}
export function attachFileStart(): AttachFileStartAction {
  return {
    type: CommonActionType.AttachFileStart
  };
}
export function attachFileSuccess(fileVMs: FileViewModel[]): AttachFileSuccessAction {
  return {
    type: CommonActionType.AttachFileSuccess,
    fileVMs: fileVMs
  };
}

export function attachFileFailure(errorMessage: string): AttachFileFailureAction {
  return {
    type: CommonActionType.AttachFileFailure,
    errorMessage: errorMessage
  };
}

export function initGetResult(id: number): InitGetResultAction {
  return {
    type: CommonActionType.AbstractGetInit,
    idToGet: id
  };
}

export function getById<TtoGet>(id: number, getter: (id: number) => Promise<TtoGet>) {
  return async (dispatch: Dispatch<AppState>): Promise<TtoGet | undefined> => {
    // dispatch init get by id for type T
    dispatch(initGetResult(id));
    try {
      const result: TtoGet = await getter(id);
      dispatch(abstractGetResultSuccess(id, result));
      return result;
      // dispatch success/store action, that stores this in a temporary location
    } catch (e) {
      // dispatch get failure
      dispatch(
        getResultFailure(
          id,
          config.environment === 'Local' ? e : 'Network error while loading data. Please try again later.'
        )
      );
      return;
    }
  };
}

export function abstractGetResultSuccess<ToGet>(id: number, resultToStore: ToGet): SuccessGetResultAction<ToGet> {
  return {
    type: CommonActionType.AbstractGetSuccess,
    resultToStore: resultToStore,
    id: id
  };
}

export function checkZipCode(zip: number) {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      var result = await utilityService.verifyZipCode(zip);
      if (result) {
        return result;
      } else {
        dispatch(getResultFailure(zip, `We're sorry, but FNHW does not offer coverage in this area.`));
      }
      return result;
    } catch (e) {
      dispatch(
        getResultFailure(
          zip,
          config.environment === 'Local' ? e : 'Network error while loading data. Please try again later.'
        )
      );
      return false;
    }
  };
}

export function getResultFailure(id: number, message: string): GetResultFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={`${message}`} />, ToastOptions.Error);
  return {
    type: CommonActionType.AbstractGetFailure,
    idWithError: id,
    message: message
  };
}

export function resetFileAttachForm() {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset(FormNames.FileUploadForm));
    dispatch(resetFileAttachSuccess());
  };
}

export function resetFileAttachSuccess(): ResetFileAttachFormAction {
  return {
    type: CommonActionType.ResetFileAttachForm
  };
}

/* abstract networking */
export interface RequestActionOptions {
  readonly hasToast: boolean;
  readonly supressErrors: boolean;
  readonly toastSuccessMessage: string;
  readonly overrideFailMessage: string;
  readonly includeErrorDetail: boolean;
  readonly loadingAction?: (loading: boolean) => Action;
}

export function startNetworking(options?: Partial<RequestActionOptions>) {
  return {
    type: CommonActionType.NetworkStart
    // possibly add some kind of ID here?
  };
}

export function failNetworking(message: string, options?: Partial<RequestActionOptions>) {
  if (!options || !options.supressErrors) {
    let errorMessage = (options || {}).overrideFailMessage || 'Network error. Please try again later.';
    if (options && options.includeErrorDetail) {
      errorMessage += ' ' + message;
    }
    toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  }

  return {
    type: CommonActionType.NetworkFailure,
    message: message
  };
}
export function requestAction<ReturnT, ActionT extends Action>(
  networkCall: () => Promise<ReturnT>,
  successActionBuilder: Redux.ActionCreator<ActionT>,
  requestOptions?: Partial<RequestActionOptions>
): (dispatch: Dispatch<AppState>) => Promise<ActionT | undefined> {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(startNetworking(requestOptions));
    if (requestOptions && requestOptions.loadingAction) {
      dispatch(requestOptions.loadingAction(true));
    }
    try {
      const result: ReturnT = await networkCall();
      if (requestOptions && requestOptions.hasToast) {
        toast.success(
          <ToastComponent type={ToastType.Success} message={requestOptions.toastSuccessMessage || 'Save Successful'} />,
          ToastOptions.Success
        );
      }
      if (requestOptions && requestOptions.loadingAction) {
        dispatch(requestOptions.loadingAction(false));
      }
      return dispatch(successActionBuilder(result));
    } catch (e) {
      if (requestOptions && requestOptions.loadingAction) {
        dispatch(requestOptions.loadingAction(false));
      }
      dispatch(failNetworking(e.toString(), requestOptions));
      return;
    }
  };
}

export function setFileListLoading(loading: boolean): SetFileListLoadingAction {
  return {
    type: CommonActionType.SetFileListLoading,
    isFileListLoading: loading
  };
}



// WEBPACK FOOTER //
// ./src/common/components/common.actions.tsx